/**
 * The clubs and players every fixture stands in for.
 *
 * One list, shared by every fixture: a roster that calls a club "Redbridge"
 * while the fixture list calls it "Demo United" reads as two half-finished
 * screens rather than one product. Names are invented but plausible, so a
 * screen with no data behind it still looks like the real thing.
 */

export interface MockClub {
  teamId: number;
  name: string;
  shortCode: string;
  venue: string;
}

export const MOCK_CLUBS: readonly MockClub[] = [
  { teamId: 900101, name: 'North London', shortCode: 'NLD', venue: 'Ashburn Park' },
  { teamId: 900102, name: 'Harbour City', shortCode: 'HRC', venue: 'Dockside Arena' },
  { teamId: 900103, name: 'Redbridge', shortCode: 'RBG', venue: 'Bridge Lane' },
  { teamId: 900104, name: 'Kingsport', shortCode: 'KGP', venue: 'The Kingsyard' },
  { teamId: 900105, name: 'Eastwood', shortCode: 'EWD', venue: 'Eastwood Green' },
  { teamId: 900106, name: 'Riverton', shortCode: 'RVT', venue: 'Riverside Ground' },
  { teamId: 900107, name: 'Westford', shortCode: 'WSF', venue: 'Fordway Stadium' },
  { teamId: 900108, name: 'Brighton Vale', shortCode: 'BVL', venue: 'Vale Park' },
  { teamId: 900109, name: 'Mersey Blue', shortCode: 'MSY', venue: 'Blue Harbour' },
  { teamId: 900110, name: 'Crown Athletic', shortCode: 'CRA', venue: 'Crown Field' },
  { teamId: 900111, name: 'Forest Gate', shortCode: 'FGT', venue: 'Gatewood' },
  { teamId: 900112, name: 'Southbank', shortCode: 'SBK', venue: 'Bankside' },
  { teamId: 900113, name: 'Ironworks', shortCode: 'IRW', venue: 'The Foundry' },
  { teamId: 900114, name: 'Albion Town', shortCode: 'ALB', venue: 'Albion Road' },
  { teamId: 900115, name: 'Wanderers', shortCode: 'WND', venue: 'Highfield' },
  { teamId: 900116, name: 'Royal Park', shortCode: 'RYP', venue: 'The Crescent' },
  { teamId: 900117, name: 'City Borough', shortCode: 'CTB', venue: 'Borough Lane' },
  { teamId: 900118, name: 'Seaside', shortCode: 'SEA', venue: 'The Promenade' },
  { teamId: 900119, name: 'Valley United', shortCode: 'VLY', venue: 'Valley Ground' },
  { teamId: 900120, name: 'Oakfield', shortCode: 'OAK', venue: 'Oakfield Park' },
];

/** Club names only, for the places that carry a label rather than a record. */
export const MOCK_CLUB_NAMES: readonly string[] = MOCK_CLUBS.map((club) => club.name);

/** Never fails: every index wraps, so callers can index by a loop counter. */
export const clubAt = (index: number): MockClub =>
  MOCK_CLUBS[((index % MOCK_CLUBS.length) + MOCK_CLUBS.length) % MOCK_CLUBS.length] as MockClub;

export const MOCK_PLAYER_NAMES: readonly string[] = [
  'Elliot Grant', 'Tomas Vale', 'Noah Bennett', 'Marcus Sterling', 'Daniel Okoro',
  'James Walker', 'Samuel Cole', 'Victor Martins', 'Michael Adeyemi', 'Leo Hart',
  'Rafael Costa', 'Owen Price', 'Nico Silva', 'Ethan Ford', 'Kieran Moss',
  'Andre Bell', 'Mason Reid', 'Julian Cross', 'Ben Carter', 'Felix King',
  'Isaac Shaw', 'Mateo Dunn', 'Callum Reed', 'Joel Harvey', 'Kofi Mensah',
  'Adam Whitfield', 'Luca Moretti', 'Dami Balogun', 'Ryan Kelso', 'Theo Marchand',
  'Sean Gallagher', 'Omar Haddad', 'Pavel Novak', 'Jude Ellery', 'Nathan Boateng',
];

/**
 * A stable name for a player index.
 *
 * Past the end of the list a surname-style suffix keeps every name unique
 * without falling back to "Player 41", which reads as placeholder text.
 */
export function playerNameAt(index: number): string {
  const base = MOCK_PLAYER_NAMES[index % MOCK_PLAYER_NAMES.length] as string;
  const lap = Math.floor(index / MOCK_PLAYER_NAMES.length);

  if (lap === 0) return base;

  // "Elliot Grant" -> "E. Grant Jr" / "E. Grant III" rather than a number.
  const suffixes = ['Jr', 'III', 'IV', 'V'];
  return `${base} ${suffixes[(lap - 1) % suffixes.length] as string}`;
}
